import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { CheckCircle2, Download } from 'lucide-react';
import SignatureModal from './SignatureModal';
import ContractAnnotationToolbar from './ContractAnnotationToolbar';
import ContractFieldPanel from './ContractFieldPanel';
import requests from '../../../services/httpServices';
import {
  logContractAuditEvent,
  downloadContractCertificate,
  capturePreciseLocation,
} from '../../../services/contractAuditService';
import {
  CONTRACT_TOOLS,
  DEFAULT_FADE_SECONDS,
  getToolById,
  getColorById,
} from './contractAnnotationTools';
import { useMeetingState } from '../../../context/MeetingStateContext';

function sanitizeUrl(url) {
  if (!url) return url;
  if (url.startsWith('http://')) return url.replace('http://', 'https://');
  return url;
}

/**
 * Overlay component that renders HTML <input> elements on top of the
 * Apryse WebViewer, positioned to match the PDF page coordinates.
 * Uses requestAnimationFrame to continuously sync with the viewer's
 * scroll and zoom state.
 */
function InlineFieldOverlay({ fields, documentViewerRef, instanceRef, viewerRef, onFieldChange, liveValues }) {
  const [positions, setPositions] = useState([]);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!fields || !fields.length) return;
    let rafId;
    let lastZoom = 0;
    let lastScrollTop = 0;
    let lastScrollLeft = 0;
    let logOnce = true;

    const update = () => {
      const docViewer = documentViewerRef.current;
      const instance = instanceRef.current;
      if (!docViewer || !instance) {
        rafId = requestAnimationFrame(update);
        return;
      }

      const zoom = docViewer.getZoomLevel();
      const scrollEl = docViewer.getScrollViewElement();
      const scrollTop = scrollEl?.scrollTop || 0;
      const scrollLeft = scrollEl?.scrollLeft || 0;

      // Only recalculate if something changed
      if (zoom === lastZoom && scrollTop === lastScrollTop && scrollLeft === lastScrollLeft) {
        rafId = requestAnimationFrame(update);
        return;
      }
      lastZoom = zoom;
      lastScrollTop = scrollTop;
      lastScrollLeft = scrollLeft;

      // Calculate positions using zoom, page dimensions, and scroll.
      // No iframe DOM access — pure math based on Apryse document info.
      const doc = docViewer.getDocument();
      if (!doc) {
        rafId = requestAnimationFrame(update);
        return;
      }

      // Use Apryse's DisplayMode.pageToWindow() to convert page coordinates
      // to viewer window coordinates. This handles all internal layout
      // (margins, centering, gaps, rotation) correctly at any zoom level.
      let displayMode = null;
      try {
        displayMode = docViewer.getDisplayModeManager().getDisplayMode();
      } catch (_) {}

      const newPositions = fields.map((f) => {
        try {
          if (displayMode?.pageToWindow) {
            const topLeft = displayMode.pageToWindow(
              { x: f.rawX, y: f.rawY },
              f.pageIndex
            );
            const bottomRight = displayMode.pageToWindow(
              { x: f.rawX + f.rawW, y: f.rawY + f.rawH },
              f.pageIndex
            );
            return {
              fieldId: f.fieldId,
              fieldType: f.fieldType,
              label: f.label,
              placeholder: f.placeholder || '',
              role: f.role || 'participant',
              value: f.value,
              readOnly: !!f.readOnly,
              left: topLeft.x - scrollLeft,
              top: topLeft.y - scrollTop,
              width: bottomRight.x - topLeft.x,
              height: bottomRight.y - topLeft.y,
              fontSize: Math.min(Math.max((bottomRight.y - topLeft.y) * 0.5, 10), 18),
            };
          }
        } catch (_) {}

        // Fallback: manual calculation
        const doc = docViewer.getDocument();
        let pageTop = 10;
        if (doc) {
          for (let p = 1; p < f.pageIndex; p++) {
            const pi = doc.getPageInfo(p);
            if (pi) pageTop += pi.height * zoom + 20;
          }
        }
        const viewerWidth = scrollEl?.clientWidth || 800;
        const pi = doc?.getPageInfo(f.pageIndex);
        const pagePixelW = pi ? pi.width * zoom : 612 * zoom;
        const pageLeft = Math.max(0, (viewerWidth - pagePixelW) / 2);
        return {
          fieldId: f.fieldId,
          fieldType: f.fieldType,
          label: f.label,
          value: f.value,
          readOnly: !!f.readOnly,
          left: pageLeft + f.rawX * zoom - scrollLeft,
          top: pageTop + f.rawY * zoom - scrollTop,
          width: f.rawW * zoom,
          height: f.rawH * zoom,
          fontSize: Math.min(Math.max(f.rawH * zoom * 0.5, 10), 18),
        };
      });

      setPositions(newPositions);
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [fields, documentViewerRef, instanceRef, viewerRef]);

  if (!fields || !fields.length || !positions.length) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 20,
      }}
    >
      {positions.map((pos) =>
        pos.readOnly ? (
          // Read-only field (host's pre-filled value shown to participant)
          <div
            key={pos.fieldId}
            style={{
              position: 'absolute',
              left: pos.left,
              top: pos.top,
              width: pos.width,
              height: pos.height,
              fontSize: pos.fontSize,
              padding: '0 4px 0 4px',
              boxSizing: 'border-box',
              color: '#000',
              fontFamily: 'sans-serif',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              lineHeight: 1,
              overflow: 'hidden',
              zIndex: 30,
            }}
          >
            {(liveValues && liveValues[pos.fieldId]) ?? pos.value}
          </div>
        ) : (() => {
          // Tint background by role so hosts/participants can tell at a glance
          // which fields they own. Kept light so the underlying PDF still reads
          // through and the final export doesn't look shaded.
          const isHostField = pos.role === 'host';
          const restBg = isHostField
            ? 'rgba(120, 80, 200, 0.10)'   // host — light purple
            : 'rgba(60, 130, 240, 0.10)';  // participant — light blue
          const restBorder = isHostField
            ? 'rgba(120, 80, 200, 0.45)'
            : 'rgba(60, 130, 240, 0.45)';
          const focusBorder = isHostField ? '#7c3aed' : '#2563eb';
          const focusShadow = isHostField
            ? '0 0 0 2px rgba(124, 58, 237, 0.30)'
            : '0 0 0 2px rgba(37, 99, 235, 0.30)';
          return (
            <input
              key={pos.fieldId}
              type={pos.fieldType === 'date' ? 'date' : 'text'}
              placeholder={pos.placeholder || ''}
              value={(liveValues && liveValues[pos.fieldId]) ?? pos.value ?? ''}
              onChange={(e) => onFieldChange(pos.fieldId, e.target.value)}
              style={{
                position: 'absolute',
                left: pos.left,
                top: pos.top,
                width: pos.width,
                height: pos.height,
                fontSize: pos.fontSize,
                paddingTop: Math.max(pos.height - pos.fontSize - 2, 0),
                paddingBottom: 0,
                paddingLeft: 4,
                paddingRight: 4,
                lineHeight: `${pos.fontSize}px`,
                boxSizing: 'border-box',
                border: 'none',
                borderBottom: `1px solid ${restBorder}`,
                borderRadius: 0,
                background: restBg,
                color: '#000',
                outline: 'none',
                zIndex: 30,
                fontFamily: 'sans-serif',
                pointerEvents: 'auto',
              }}
              onFocus={(e) => {
                e.target.style.borderBottom = `1.5px solid ${focusBorder}`;
                e.target.style.boxShadow = focusShadow;
              }}
              onBlur={(e) => {
                e.target.style.borderBottom = `1px solid ${restBorder}`;
                e.target.style.boxShadow = 'none';
              }}
            />
          );
        })()
      )}
    </div>
  );
}

export default function ApryseContractRenderer({
  contract,
  onClose,
  onUpdate,
  isHost = false,
  currentUserName = 'User',
  currentUserId = '',
  currentUserEmail = '',
  meetingId = '',
  onSendToParticipants,
  onStopPresenting,
}) {
  const meetingState = useMeetingState();

  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const instanceRef = useRef(null);
  const documentViewerRef = useRef(null);
  const annotManagerRef = useRef(null);
  const applyingRemoteRef = useRef(false);
  const scrollThrottleRef = useRef(0);
  const fadeTimersRef = useRef(new Map());

  const [activeToolId, setActiveToolId] = useState('pen');
  const [activeColorId, setActiveColorId] = useState('red');
  const [thickness, setThickness] = useState(2);
  const [fadeSeconds, setFadeSeconds] = useState(DEFAULT_FADE_SECONDS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState(null);
  const [presented, setPresented] = useState(false);
  // Host-side live indicator: who has signed their private copy.
  const [signedNotices, setSignedNotices] = useState([]);
  const [showSignedList, setShowSignedList] = useState(false);
  // Submission confirmation (participant-facing full-screen card)
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Minimized: contract is hidden but state preserved so user can return.
  const [minimized, setMinimized] = useState(false);
  const minimizedRef = useRef(false);
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);
  // Inline text/date input positions (React-rendered overlay on top of viewer)
  const [inlineInputPositions, setInlineInputPositions] = useState([]);

  // --- Editable field form state ---
  // Host: sees ALL fields as editable (host fields for themselves, participant
  //       fields for filling on behalf when a participant is selected).
  // Participant: sees only participant-role fields as editable; host fields
  //              show as read-only with host's filled values.
  const editableFields = contract?.editable_fields || [];
  const myFields = isHost
    ? editableFields // host can interact with all fields
    : editableFields.filter((f) => f.role !== 'host');
  const hasFields = myFields.length > 0;
  // Initialize fieldValues from any pre-filled values on the contract
  // (e.g. host fills their fields then presents — those values arrive
  // as field.value on the editable_fields array).
  const [fieldValues, setFieldValues] = useState(() => {
    const init = {};
    (contract?.editable_fields || []).forEach((f) => {
      if (f.value) init[f.id] = f.value;
    });
    return init;
  });
  const fieldValuesRef = useRef(fieldValues);
  useEffect(() => { fieldValuesRef.current = fieldValues; }, [fieldValues]);
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldPanelCollapsed, setFieldPanelCollapsed] = useState(false);

  // --- Host-side: live tracking of each participant's filled values ---
  // Keyed by participantId: { fieldId: value }
  const [participantFieldValues, setParticipantFieldValues] = useState({});
  // List of participants who have interacted with the contract
  const [activeParticipants, setActiveParticipants] = useState([]);
  // Which participant the host is currently viewing (null = host's own view)
  const [selectedParticipantId, setSelectedParticipantId] = useState(null);
  // Whether the host's "view as participant" panel is open
  const [showParticipantPanel, setShowParticipantPanel] = useState(false);
  // Host-side: submission URL per participantId (for download button)
  const [submissionsByParticipant, setSubmissionsByParticipant] = useState({});

  // --- Draft save/load: declared further down after contractElementId ---
  const draftLoadedRef = useRef(false);
  const draftSaveTimerRef = useRef(null);
  const draftUserId = currentUserId || (isHost ? 'host' : 'participant');

  // Keep the fade duration mutable without re-registering listeners.
  const fadeSecondsRef = useRef(fadeSeconds);
  useEffect(() => {
    fadeSecondsRef.current = fadeSeconds;
  }, [fadeSeconds]);

  // Document fingerprint (SHA-256 of PDF bytes). Computed once after the
  // document loads so every subsequent audit event for this session can
  // reference the exact version being viewed.
  const documentVersionRef = useRef('');

  // Promise for a precise-location capture kicked off in a direct user
  // gesture (click on Sign / Decline). Browsers require geolocation to
  // be requested in response to a user action; awaiting later works.
  const pendingLocationRef = useRef(null);

  const contractElementId =
    contract?._id || contract?.id || contract?.elementId || 'contract';

  // --- Draft save/load: persist in-progress field values to DB per session ---
  // Participants only. Host drafts were leaking across meeting sessions
  // (meetingId is the stable roomId), re-populating the contract with old
  // signatures/text every time the host re-opened it in a new meeting. Host
  // work is preserved via React state for the active session; closing the
  // modal intentionally clears it.
  useEffect(() => {
    if (draftLoadedRef.current) return;
    if (isHost) { draftLoadedRef.current = true; return; }
    if (!contractElementId || !meetingId || !draftUserId) return;
    const loadDraft = async () => {
      try {
        const res = await requests.get(
          `/contract/draft/${encodeURIComponent(contractElementId)}/${encodeURIComponent(meetingId)}/${encodeURIComponent(draftUserId)}`
        );
        if (res?.success && res.draft?.fieldValues) {
          setFieldValues((prev) => ({ ...res.draft.fieldValues, ...prev }));
          // Participants: rebroadcast saved draft values so the host picks
          // them up immediately after re-presenting.
          if (!isHost) {
            setTimeout(() => {
              if (typeof meetingState?.emitContractEvent !== 'function') return;
              Object.entries(res.draft.fieldValues || {}).forEach(([fieldId, value]) => {
                if (value === undefined || value === null || value === '') return;
                meetingState.emitContractEvent({
                  contractId: contractElementId,
                  type: 'fieldValue',
                  payload: {
                    fromId: currentUserId || '',
                    fromName: currentUserName || 'Participant',
                    fromRole: 'participant',
                    forParticipantId: currentUserId || '',
                    fieldId,
                    value,
                  },
                });
              });
            }, 800);
          }
        }
      } catch (err) {
        console.warn('[Contract] Failed to load draft:', err?.message);
      } finally {
        draftLoadedRef.current = true;
      }
    };
    loadDraft();
  }, [contractElementId, meetingId, draftUserId]);

  // Debounced draft save. Participants save their own field values. Host
  // only saves values they've filled on BEHALF of participants (keyed by
  // that participant's id) — host's own fieldValues are session-scoped and
  // intentionally not persisted.
  useEffect(() => {
    if (!draftLoadedRef.current) return;
    if (!contractElementId || !meetingId) return;
    if (submissionResult) return; // already submitted, no more drafts
    if (isHost) return; // host path handled by the participantFieldValues effect

    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      requests
        .post('/contract/draft', {
          contractElementId,
          meetingId,
          participantId: draftUserId,
          fieldValues,
          role: 'participant',
        })
        .catch((err) => console.warn('[Contract] Draft save failed:', err?.message));
    }, 800);

    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, [fieldValues, contractElementId, meetingId, draftUserId, isHost, submissionResult]);

  // Host-on-behalf drafts: whenever participantFieldValues changes, persist
  // each participant's values under their own draft key so the participant
  // sees the host's filled-in values when they open the contract.
  const hostBehalfSaveTimersRef = useRef(new Map());
  useEffect(() => {
    if (!isHost) return;
    if (!contractElementId || !meetingId) return;
    if (submissionResult) return;

    Object.entries(participantFieldValues || {}).forEach(([pid, values]) => {
      if (!pid || !values) return;
      const existing = hostBehalfSaveTimersRef.current.get(pid);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        requests
          .post('/contract/draft', {
            contractElementId,
            meetingId,
            participantId: pid,
            fieldValues: values,
            role: 'participant',
          })
          .catch((err) => console.warn('[Contract] Host-on-behalf draft save failed:', err?.message));
      }, 800);
      hostBehalfSaveTimersRef.current.set(pid, t);
    });

    return () => {
      hostBehalfSaveTimersRef.current.forEach((t) => clearTimeout(t));
    };
  }, [participantFieldValues, isHost, contractElementId, meetingId, submissionResult]);

  // Host-side: pre-populate submissions on mount for this meeting so
  // participants who already submitted (before the host opened the
  // contract) still appear with a download link in the dropdown.
  // Use the meeting session start time so we only show submissions from
  // THIS session (meetingId is the stable roomId, so old sessions would
  // otherwise bleed through).
  const sessionStartedAt = meetingState?.meetingStartedAt;
  useEffect(() => {
    if (!isHost || !meetingId || !contractElementId) return;
    let cancelled = false;
    requests
      .get(`/contract/submissions/${encodeURIComponent(meetingId)}`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.submissions) ? res.submissions : [];
        const sessionStart = sessionStartedAt ? new Date(sessionStartedAt).getTime() : 0;
        const mine = list.filter((s) => {
          if (String(s.contractElementId) !== String(contractElementId)) return false;
          // Only include submissions from the current meeting session
          if (sessionStart && s.createdAt) {
            return new Date(s.createdAt).getTime() >= sessionStart;
          }
          return true;
        });
        if (!mine.length) return;
        setSubmissionsByParticipant((prev) => {
          const next = { ...prev };
          for (const s of mine) {
            if (!s.participantId || !s.submissionUrl) continue;
            next[s.participantId] = {
              url: s.submissionUrl,
              name: s.participantName || 'Participant',
              at: s.createdAt ? new Date(s.createdAt).getTime() : Date.now(),
            };
          }
          return next;
        });
      })
      .catch((err) => console.warn('[Contract] load submissions failed', err?.message));
    return () => { cancelled = true; };
  }, [isHost, meetingId, contractElementId, sessionStartedAt]);

  // Host-side: listen for participant submissions to toast + track download
  // URLs so they can be surfaced in the participant dropdown.
  useEffect(() => {
    if (!isHost) return;
    const sock = meetingState?.socket;
    if (!sock) return;
    const handler = (data) => {
      if (!data || data.contractElementId !== contractElementId) return;
      const sub = data.submission;
      if (!sub) return;
      const pid = sub.participantId || '';
      const pname = sub.participantName || 'Participant';
      const url = sub.submissionUrl || '';
      if (pid && url) {
        setSubmissionsByParticipant((prev) => ({ ...prev, [pid]: { url, name: pname, at: Date.now() } }));
      }
      toast.success(`${pname} submitted the contract`, { duration: 6000 });
      try {
        const audio = new Audio('/audio/chime.wav');
        audio.volume = 0.6;
        audio.play().catch(() => {});
      } catch (_) {}
    };
    sock.on('contract:submission-added', handler);
    return () => sock.off('contract:submission-added', handler);
  }, [isHost, meetingState?.socket, contractElementId]);

  // ============================================================
  // Tool styling helpers
  // ============================================================
  const applyToolStyles = useCallback(
    (toolId, colorId, thicknessOverride) => {
      const instance = instanceRef.current;
      if (!instance) return;
      const { Core } = instance;
      const preset = getToolById(toolId);
      if (!preset) return;
      const color = getColorById(colorId);

      const tool = Core.documentViewer.getTool(preset.toolName);
      if (!tool) return;

      const styles = {
        StrokeColor: new Core.Annotations.Color(
          color.rgb.R,
          color.rgb.G,
          color.rgb.B,
          color.rgb.A
        ),
        StrokeThickness:
          thicknessOverride != null ? thicknessOverride : preset.defaultThickness,
        Opacity: preset.defaultOpacity,
      };
      if (!preset.filled) {
        styles.FillColor = new Core.Annotations.Color(0, 0, 0, 0);
      }
      tool.setStyles(styles);
    },
    []
  );

  // ============================================================
  // Ephemeral fade: after N seconds, tween opacity then delete.
  // Permanent annotations (signatures) are skipped.
  // ============================================================
  const isPermanentAnnotation = (annotation) => {
    if (!annotation) return false;
    try {
      if (annotation.getCustomData && annotation.getCustomData('permanent') === 'true') return true;
    } catch (_) {}
    const type = annotation.Subject || annotation.elementName || '';
    if (type === 'Stamp' || type === 'stamp') return true;
    return false;
  };

  const scheduleFade = useCallback((annotation) => {
    const annotManager = annotManagerRef.current;
    if (!annotManager) return;
    if (fadeTimersRef.current.has(annotation.Id)) return;
    if (isPermanentAnnotation(annotation)) return;

    const handle = { timeout: null, interval: null };
    handle.timeout = setTimeout(() => {
      const steps = 8;
      let i = 0;
      const startOpacity = annotation.Opacity ?? 1;
      handle.interval = setInterval(() => {
        // If the fade was cancelled (e.g. user pressed Clear), bail out.
        if (!fadeTimersRef.current.has(annotation.Id)) {
          clearInterval(handle.interval);
          return;
        }
        i++;
        annotation.Opacity = startOpacity * (1 - i / steps);
        try {
          annotManager.redrawAnnotation(annotation);
        } catch (_) {}
        if (i >= steps) {
          clearInterval(handle.interval);
          try {
            annotManager.deleteAnnotation(annotation, {
              force: true,
              imported: false,
            });
          } catch (_) {}
          fadeTimersRef.current.delete(annotation.Id);
        }
      }, 50);
    }, fadeSecondsRef.current * 1000);

    fadeTimersRef.current.set(annotation.Id, handle);
  }, []);

  // ============================================================
  // Outbound sync helpers
  // ============================================================
  const emitContractEvent = useCallback(
    (type, payload) => {
      if (!meetingState?.emitContractEvent) return;
      meetingState.emitContractEvent({
        contractId: contractElementId,
        type,
        payload,
      });
    },
    [meetingState, contractElementId]
  );

  // ============================================================
  // WebViewer bootstrap
  // ============================================================
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!contract?.file_url) {
        setError('No file URL on contract element');
        setLoading(false);
        return;
      }

      try {
        const mod = await import('@pdftron/webviewer');
        const WebViewer = mod.default;
        if (cancelled || !viewerRef.current) return;

        // Always proxy through backend — matches PresentationStageRenderer
        // because the S3 bucket doesn't emit CORS headers Apryse needs.
        const proxyBase = process.env.REACT_APP_BASE_URL || '';
        const rawUrl = sanitizeUrl(contract.file_url);
        const proxiedUrl = `${proxyBase.replace(/\/$/, '')}/proxy/resource?url=${encodeURIComponent(rawUrl)}`;

        const disabledElements = [
          // Main header + the whole inner annotation tools row
          'header',
          'toolsHeader',
          'ribbons',
          'ribbonsDropdown',
          'toolbarGroup-Annotate',
          'toolbarGroup-Shapes',
          'toolbarGroup-Insert',
          'toolbarGroup-Edit',
          'toolbarGroup-View',
          'toolbarGroup-Forms',
          'toolbarGroup-FillAndSign',
          // Left/top-left icons: document/thumbnail/outline panels
          'leftPanelButton',
          'leftPanel',
          'thumbnailsPanelButton',
          'thumbnailPanelButton',
          'thumbnailPanelToggleButton',
          'outlinesPanelButton',
          'bookmarksPanelButton',
          'layersPanelButton',
          // Selection / marquee tool icon
          'selectToolButton',
          'marqueeToolButton',
          'panToolButton',
          // Search + notes panel icons (top-right)
          'searchButton',
          'searchPanel',
          'notesPanel',
          'notesPanelToggle',
          'toggleNotesButton',
          'notesPanelButton',
          // Misc overlays + config menus
          'viewControlsButton',
          'viewControlsOverlay',
          'menuButton',
          'menuOverlay',
          'annotationStyleEditButton',
          'stylePopup',
          'pageNavOverlay',
          // Extras
          'fullScreenButton',
          'printButton',
          'downloadButton',
          'toggleCompareButton',
          'rotateButton',
          // Top-left logo / document icon only (keep zoom visible)
          'apryseLogoButton',
          'filePickerButton',
          'newDocumentButton',
          'mainMenuLabel',
          'viewFileButton',
          'settingsButton',
          // Selection / marquee / text-select tool icons
          'selectToolButton',
          'textSelectButton',
          'panToolButton',
          'marqueeToolButton',
          // Snipping/crop/selection marquee tool
          'snippingToolButton',
          'snippingToolGroupButton',
          'cropToolButton',
          'cropToolGroupButton',
          'annotationCropButton',
          // Floating "shapes" toolbar — confirmed dataElement IDs from Apryse UI
          'toolsOverlay',
          'shapesGroupedItems',
          'shapesToolsGroupedItems',
          'annotateGroupedItems',
          'annotateToolsGroupedItems',
          'shapeToolGroupButton',
          'rectangleToolButton',
          'rectangleToolGroupButton',
          'ellipseToolButton',
          'ellipseToolGroupButton',
          'polygonToolButton',
          'polygonToolGroupButton',
          'polygonCloudToolGroupButton',
          'cloudToolButton',
          'cloudToolGroupButton',
          'polylineToolButton',
          'polyLineToolGroupButton',
          'polylineToolGroupButton',
          'lineToolButton',
          'lineToolGroupButton',
          'arrowToolButton',
          'arrowToolGroupButton',
          'freeHandToolButton',
          'freeHandToolGroupButton',
          'freeHandHighlightToolButton',
          'freeHandHighlightToolGroupButton',
          'freeTextToolGroupButton',
          'stickyToolGroupButton',
          'highlightToolGroupButton',
          'underlineToolGroupButton',
          'squigglyToolGroupButton',
          'strikeoutToolGroupButton',
          'signatureToolGroupButton',
          'stampToolGroupButton',
          'rubberStampToolGroupButton',
          'calloutToolGroupButton',
          'eraserToolButton',
          'undoButton',
          'redoButton',
          'toolStylePopup',
          'annotationPopup',
          'annotationStylePopup',
          'contextMenuPopup',
          'colorPalette',
          'colorPaletteTabs',
        ];

        const instance = await WebViewer(
          {
            path: '/lib/webviewer',
            licenseKey: process.env.REACT_APP_APRYSE_LICENSE,
            enableAnnotations: true,
            enableFilePicker: false,
            disabledElements,
            disableVirtualDisplayMode: true,
            fullAPI: true,
          },
          viewerRef.current
        );

        // Explicitly load the document with an extension hint so the
        // proxied URL (which has no .pdf suffix) is recognized.
        instance.UI.loadDocument(proxiedUrl, {
          extension: 'pdf',
          filename: contract?.name || 'contract.pdf',
        });

        if (cancelled) return;

        instanceRef.current = instance;
        const { UI, Core } = instance;
        const { documentViewer, annotationManager } = Core;
        documentViewerRef.current = documentViewer;
        annotManagerRef.current = annotationManager;

        UI.setLayoutMode(UI.LayoutMode.Continuous);
        UI.disableElements(disabledElements);
        // Apryse v11 Modular UI: replace the header entirely with an empty
        // array so ALL default icons disappear (logo, thumbnail toggle,
        // selection tool, zoom — everything). Our custom toolbar handles UI.
        try {
          if (typeof UI.setModularHeaders === 'function') {
            UI.setModularHeaders([]);
          } else if (typeof UI.setHeaderItems === 'function') {
            UI.setHeaderItems((header) => {
              header.headers.default = [];
              if (header.headers.toolsHeader) header.headers.toolsHeader = [];
            });
          }
        } catch (e) {
          console.warn('[ContractApryse] setModularHeaders failed:', e.message);
        }
        // Aggressively hide stubborn icons via CSS + MutationObserver —
        // some Apryse UI elements get added AFTER init, so we need to watch.
        try {
          const getIframeDoc = () =>
            instance.UI?.iframeWindow?.document
            || viewerRef.current?.querySelector('iframe')?.contentDocument;
          const iframeDoc = getIframeDoc();
          if (iframeDoc) {
            const HIDE_DATA_ELEMENTS = [
              'apryseLogoButton', 'filePickerButton', 'newDocumentButton',
              'mainMenuLabel', 'viewFileButton', 'settingsButton',
              'selectToolButton', 'textSelectButton', 'panToolButton',
              'marqueeToolButton', 'snippingToolButton', 'snippingToolGroupButton',
              'cropToolButton', 'cropToolGroupButton', 'annotationCropButton',
              'toolsOverlay', 'shapesGroupedItems', 'shapesToolsGroupedItems',
              'annotateGroupedItems', 'annotateToolsGroupedItems',
              'searchButton', 'searchPanel', 'notesPanelToggle',
              'notesPanelButton', 'toggleNotesButton', 'leftPanelButton',
              'thumbnailsPanelButton', 'thumbnailPanelToggleButton',
              'menuButton', 'fullScreenButton',
            ];
            const selectorList = HIDE_DATA_ELEMENTS
              .map((id) => `[data-element="${id}"]`)
              .join(',\n');
            const style = iframeDoc.createElement('style');
            style.textContent = `
              ${selectorList} {
                display: none !important;
                visibility: hidden !important;
                width: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
                pointer-events: none !important;
              }
            `;
            iframeDoc.head.appendChild(style);

            // Also kill them via JS in case CSS cascade is overridden
            const killAll = () => {
              HIDE_DATA_ELEMENTS.forEach((id) => {
                iframeDoc.querySelectorAll(`[data-element="${id}"]`).forEach((el) => {
                  el.style.display = 'none';
                  el.remove?.();
                });
              });
            };
            killAll();
            // Run again after Apryse finishes its async UI mount
            setTimeout(killAll, 300);
            setTimeout(killAll, 1000);
            // Watch for any late-added elements
            const observer = new iframeDoc.defaultView.MutationObserver(() => {
              killAll();
            });
            observer.observe(iframeDoc.body, { childList: true, subtree: true });
          }
        } catch (e) {
          console.warn('[ContractApryse] CSS injection failed:', e.message);
        }
        annotationManager.setCurrentUser(isHost ? 'host' : currentUserName);

        documentViewer.addEventListener('documentLoaded', async () => {
          setLoading(false);
          // Pre-style all host tools
          CONTRACT_TOOLS.forEach((t) =>
            applyToolStyles(t.id, t.defaultColor, t.defaultThickness)
          );

          // Compute a one-time SHA-256 fingerprint of the document bytes
          // so every audit event emitted from this session is linked to
          // a specific version of the contract.
          try {
            const docObj = documentViewer.getDocument();
            const bytes = await docObj.getFileData({});
            const digest = await crypto.subtle.digest('SHA-256', bytes);
            const hex = Array.from(new Uint8Array(digest))
              .map((b) => b.toString(16).padStart(2, '0'))
              .join('');
            documentVersionRef.current = hex;
          } catch (err) {
            console.warn('[ContractApryse] documentVersion hash failed', err);
          }

          // Audit: participant opened the document
          if (!isHost) {
            logContractAuditEvent({
              contractElementId,
              meetingId,
              eventType: 'opened',
              actor: {
                id: currentUserId || '',
                name: currentUserName || 'Participant',
                email: currentUserEmail || '',
                role: 'participant',
              },
              metadata: { fileUrl: contract?.file_url || '' },
              documentVersion: documentVersionRef.current,
            });
            // Announce participant presence to host so they appear in the
            // participant selector even before typing anything.
            if (typeof emitContractEvent === 'function') {
              emitContractEvent('participantOpened', {
                participantId: currentUserId || '',
                participantName: currentUserName || 'Participant',
              });
            }
          } else {
            // Host just (re-)opened the contract. Ask any already-mounted
            // participants to rebroadcast their current field values so the
            // host sees their in-progress drafts after a stop/re-present.
            if (typeof emitContractEvent === 'function') {
              emitContractEvent('hostOpened', {
                hostId: currentUserId || '',
                hostName: currentUserName || 'Host',
              });
            }
          }

          // Create inline form fields now that the document is fully loaded
          if (createInlineFieldsRef.current) {
            createInlineFieldsRef.current(instance, documentViewer, annotationManager);
          }

          if (isHost) {
            // Default to selection tool so host can click on fields.
            // Host switches to drawing tools via the toolbar when needed.
            UI.setToolMode('AnnotationEdit');

            // Scroll sync — wired after the scroll view element is real.
            const scrollEl = documentViewer.getScrollViewElement();
            if (scrollEl) {
              const onScroll = () => {
                if (applyingRemoteRef.current) return;
                if (minimizedRef.current) return; // suppress while minimized
                const now = Date.now();
                if (now - scrollThrottleRef.current < 50) return;
                scrollThrottleRef.current = now;
                emitContractEvent('scroll', {
                  top: scrollEl.scrollTop,
                  left: scrollEl.scrollLeft,
                  height: scrollEl.scrollHeight,
                  width: scrollEl.scrollWidth,
                });
              };
              scrollEl.addEventListener('scroll', onScroll, { passive: true });
            }
          }
        });

        if (isHost) {
          documentViewer.addEventListener('zoomUpdated', (zoom) => {
            if (applyingRemoteRef.current) return;
            if (minimizedRef.current) return;
            emitContractEvent('zoom', { zoom });
          });

          documentViewer.addEventListener('pageNumberUpdated', (page) => {
            if (applyingRemoteRef.current) return;
            if (minimizedRef.current) return;
            emitContractEvent('page', { page });
          });
        }

        // Annotation handling
        //   - Host broadcasts their drawings to all participants (one-way).
        //   - Participants do NOT broadcast. Each participant's signatures
        //     are private to their own copy and their own submission.
        annotationManager.addEventListener(
          'annotationChanged',
          async (annotations, action, info) => {
            if (info?.imported) return;
            if (applyingRemoteRef.current) return;
            if (action === 'add') {
              annotations.forEach((a) => scheduleFade(a));
            }
            if (!isHost) return; // participants never rebroadcast
            // When the host has the contract minimized, Apryse can fire
            // spurious annotationChanged events as it pauses/resumes
            // rendering. Broadcasting those re-applies annotations on
            // participants at stale positions (stamps landing on the
            // cover page, etc.), so suppress outbound sync while
            // minimized.
            if (minimizedRef.current) return;
            try {
              const xfdfCommand = await annotationManager.exportAnnotationCommand();
              if (!xfdfCommand) return;
              emitContractEvent('annotation', { xfdfCommand, action });
            } catch (err) {
              console.error('[ContractApryse] export command failed', err);
            }
          }
        );

        // Inbound sync — both roles listen
        const unsubscribe = meetingState?.onContractEvent
          ? meetingState.onContractEvent(
              contractElementId,
              async ({ type, payload }) => {
                const inst = instanceRef.current;
                if (!inst) return;
                applyingRemoteRef.current = true;
                try {
                  if (type === 'scroll') {
                    const el = documentViewer.getScrollViewElement();
                    if (!el) return;
                    const ratioTop =
                      payload.height > 0 ? payload.top / payload.height : 0;
                    const ratioLeft =
                      payload.width > 0 ? payload.left / payload.width : 0;
                    el.scrollTo({
                      top: Math.round(ratioTop * el.scrollHeight),
                      left: Math.round(ratioLeft * el.scrollWidth),
                      behavior: 'auto',
                    });
                  } else if (type === 'zoom') {
                    inst.UI.setZoomLevel(payload.zoom);
                  } else if (type === 'page') {
                    documentViewer.setCurrentPage(payload.page);
                  } else if (type === 'annotation') {
                    // Incremental apply — merges adds / modifies / deletes
                    // into the local annotation set without clobbering
                    // annotations other clients have already placed.
                    if (!payload?.xfdfCommand) return;
                    const imported = await annotationManager.importAnnotationCommand(
                      payload.xfdfCommand
                    );
                    if (Array.isArray(imported) && imported.length) {
                      annotationManager.drawAnnotationsFromList(imported);
                      if (payload.action === 'add') {
                        imported.forEach((a) => scheduleFade(a));
                      }
                    }
                  } else if (type === 'hostOpened') {
                    // Host (re-)opened the contract. Participants rebroadcast
                    // their current field values so the host picks up drafts
                    // that were loaded before the host re-presented.
                    if (!isHost && typeof emitContractEvent === 'function') {
                      const currentFV = fieldValuesRef.current || {};
                      Object.entries(currentFV).forEach(([fieldId, value]) => {
                        if (value === undefined || value === null || value === '') return;
                        emitContractEvent('fieldValue', {
                          fromId: currentUserId || '',
                          fromName: currentUserName || 'Participant',
                          fromRole: 'participant',
                          forParticipantId: currentUserId || '',
                          fieldId,
                          value,
                        });
                      });
                    }
                  } else if (type === 'participantOpened') {
                    // Host: register participant as active when they open the contract
                    if (isHost && payload?.participantId) {
                      setActiveParticipants((prev) => {
                        if (prev.some((p) => p.id === payload.participantId)) return prev;
                        return [
                          ...prev,
                          {
                            id: payload.participantId,
                            name: payload.participantName || 'Participant',
                            joinedAt: Date.now(),
                          },
                        ];
                      });
                    }
                  } else if (type === 'fieldValue') {
                    // Real-time field value sync between host + participants.
                    const {
                      fromId,
                      fromName,
                      fromRole,
                      forParticipantId,
                      fieldId,
                      value,
                    } = payload || {};
                    console.log('[Contract] Received fieldValue:', payload, 'isHost:', isHost, 'currentUserId:', currentUserId);
                    if (!fieldId) return;

                    if (isHost) {
                      // Host receives a participant's update — store it
                      // under that participant's bucket.
                      if (fromRole === 'participant' && fromId) {
                        setParticipantFieldValues((prev) => ({
                          ...prev,
                          [fromId]: {
                            ...(prev[fromId] || {}),
                            [fieldId]: value || '',
                          },
                        }));
                        // Track active participant
                        setActiveParticipants((prev) => {
                          if (prev.some((p) => p.id === fromId)) return prev;
                          return [
                            ...prev,
                            { id: fromId, name: fromName || 'Participant', joinedAt: Date.now() },
                          ];
                        });
                      }
                    } else {
                      // Participant receives an update from host
                      if (fromRole === 'host') {
                        // Case 1: Host filling on behalf of THIS participant
                        if (forParticipantId && forParticipantId === (currentUserId || '')) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [fieldId]: value || '',
                          }));
                        }
                        // Case 2: Host filling their OWN field (no forParticipantId).
                        // This applies to ALL participants — they should see the
                        // host's signature/date/text update on their own copy.
                        if (!forParticipantId) {
                          setFieldValues((prev) => ({
                            ...prev,
                            [fieldId]: value || '',
                          }));
                          // For signature fields, also update the contract's
                          // editable_fields so future re-renders preserve it.
                          // (The signature stamp annotation will be created/
                          //  updated by the existing signature sync useEffect.)
                        }
                      }
                    }
                  } else if (type === 'signedNotice') {
                    if (isHost && payload?.participantId) {
                      setSignedNotices((prev) => {
                        const filtered = prev.filter(
                          (n) => n.participantId !== payload.participantId
                        );
                        return [
                          ...filtered,
                          {
                            participantId: payload.participantId,
                            participantName: payload.participantName || 'Participant',
                            at: payload.at || new Date().toISOString(),
                          },
                        ];
                      });
                      // Also register this participant as active
                      setActiveParticipants((prev) => {
                        if (prev.some((p) => p.id === payload.participantId)) return prev;
                        return [
                          ...prev,
                          {
                            id: payload.participantId,
                            name: payload.participantName || 'Participant',
                            joinedAt: Date.now(),
                          },
                        ];
                      });
                    }
                  } else if (type === 'fade') {
                    if (payload.seconds > 0) setFadeSeconds(payload.seconds);
                  } else if (type === 'clear') {
                    // Cancel pending fades so they don't ghost-back annotations
                    fadeTimersRef.current.forEach((handle) => {
                      if (handle?.timeout) clearTimeout(handle.timeout);
                      if (handle?.interval) clearInterval(handle.interval);
                    });
                    fadeTimersRef.current.clear();
                    const existing = annotationManager.getAnnotationsList();
                    if (existing.length) {
                      annotationManager.deleteAnnotations(existing, {
                        force: true,
                        imported: true,
                      });
                    }
                  }
                } catch (err) {
                  console.warn('[ContractApryse] inbound apply failed', err);
                } finally {
                  setTimeout(() => {
                    applyingRemoteRef.current = false;
                  }, 30);
                }
              }
            )
          : null;

        // Cleanup on unmount
        return () => {
          if (typeof unsubscribe === 'function') unsubscribe();
        };
      } catch (err) {
        console.error('[ContractApryse] WebViewer init failed', err);
        setError(err.message || 'Failed to initialize Apryse');
        setLoading(false);
      }
    };

    const cleanupPromise = init();

    return () => {
      cancelled = true;
      // Clear both the timeout and any active interval for each fade
      fadeTimersRef.current.forEach((handle) => {
        if (handle?.timeout) clearTimeout(handle.timeout);
        if (handle?.interval) clearInterval(handle.interval);
      });
      fadeTimersRef.current.clear();
      if (typeof cleanupPromise?.then === 'function') {
        cleanupPromise.then((fn) => typeof fn === 'function' && fn()).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.file_url]);

  // ============================================================
  // Toolbar callbacks
  // ============================================================
  const handleSelectTool = useCallback(
    (toolId) => {
      setActiveToolId(toolId);
      const preset = getToolById(toolId);
      if (!preset) return;
      setThickness(preset.defaultThickness);
      applyToolStyles(toolId, activeColorId, preset.defaultThickness);
      instanceRef.current?.UI.setToolMode(preset.toolName);
    },
    [activeColorId, applyToolStyles]
  );

  // --- Zoom controls ---
  const [currentZoom, setCurrentZoom] = useState(1);
  const handleZoomIn = useCallback(() => {
    const dv = documentViewerRef.current;
    if (!dv) return;
    const z = Math.min(dv.getZoomLevel() + 0.25, 5);
    dv.zoomTo(z);
    setCurrentZoom(z);
  }, []);
  const handleZoomOut = useCallback(() => {
    const dv = documentViewerRef.current;
    if (!dv) return;
    const z = Math.max(dv.getZoomLevel() - 0.25, 0.25);
    dv.zoomTo(z);
    setCurrentZoom(z);
  }, []);
  const handleZoomReset = useCallback(() => {
    const dv = documentViewerRef.current;
    if (!dv) return;
    dv.zoomTo(1);
    setCurrentZoom(1);
  }, []);

  // --- Search ---
  const handleSearch = useCallback((query) => {
    const instance = instanceRef.current;
    if (!instance) return;
    if (!query || !query.trim()) {
      try { instance.UI.searchTextFull('', {}); } catch (_) {}
      try { instance.Core.documentViewer.clearSearchResults(); } catch (_) {}
      return;
    }
    try {
      // searchTextFull highlights all matches across all pages
      if (typeof instance.UI.searchTextFull === 'function') {
        instance.UI.searchTextFull(query, { caseSensitive: false, wholeWord: false });
      } else if (typeof instance.UI.searchText === 'function') {
        instance.UI.searchText(query);
      }
    } catch (err) {
      console.warn('[Contract] Search failed:', err.message);
    }
  }, []);

  const handleSelectColor = useCallback(
    (colorId) => {
      setActiveColorId(colorId);
      applyToolStyles(activeToolId, colorId, thickness);
    },
    [activeToolId, thickness, applyToolStyles]
  );

  const handleThicknessChange = useCallback(
    (value) => {
      setThickness(value);
      applyToolStyles(activeToolId, activeColorId, value);
    },
    [activeToolId, activeColorId, applyToolStyles]
  );

  const handleFadeSecondsChange = useCallback(
    (value) => {
      if (!Number.isFinite(value) || value <= 0) return;
      setFadeSeconds(value);
      emitContractEvent('fade', { seconds: value });
    },
    [emitContractEvent]
  );

  const handleClearAll = useCallback(() => {
    const annotManager = annotManagerRef.current;
    if (!annotManager) return;
    // Cancel all pending fade timers + intervals so previously scheduled
    // fades don't re-render the cleared annotations as ghosts.
    fadeTimersRef.current.forEach((handle) => {
      if (handle?.timeout) clearTimeout(handle.timeout);
      if (handle?.interval) clearInterval(handle.interval);
    });
    fadeTimersRef.current.clear();
    const existing = annotManager.getAnnotationsList();
    if (existing.length) {
      annotManager.deleteAnnotations(existing, { force: true });
    }
    emitContractEvent('clear', {});
  }, [emitContractEvent]);

  const handleSign = useCallback((field = null) => {
    // Kick off precise location capture NOW — this runs inside the
    // synchronous click handler so the browser accepts it as a direct
    // user gesture and shows its permission prompt.
    try {
      pendingLocationRef.current = capturePreciseLocation();
    } catch (_) {
      pendingLocationRef.current = null;
    }
    setActiveSignatureField(field);
    setSignatureModalOpen(true);
  }, []);

  const handleFieldChange = useCallback((fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  // --- Inline form fields on the PDF (host + participant) ---
  // Fields are created inside the documentLoaded handler (see createInlineFieldsRef)
  // to guarantee the document and page info are available.
  const fieldAnnotationsRef = useRef(new Map());
  const inlineFieldsCreatedRef = useRef(false);

  // Store the creation function in a ref so documentLoaded can call it
  // with the correct instance/Core without stale closures.
  const createInlineFieldsRef = useRef(null);
  createInlineFieldsRef.current = (instance, docViewer, annotManager) => {
    if (inlineFieldsCreatedRef.current) return;
    const allFields = contract?.editable_fields || [];
    if (!allFields.length) return;
    const doc = docViewer.getDocument();
    if (!doc) return;

    const { Core } = instance;
    const Annots = Core.Annotations;
    inlineFieldsCreatedRef.current = true;

    applyingRemoteRef.current = true;
    const initValues = fieldValuesRef.current;

    // Render ALL fields on the PDF:
    // - This user's fields (myFields) → interactive (editable)
    // - Other role's fields → read-only with pre-filled values
    let pendingTextFields = null;
    allFields.forEach((field) => {
      const isMine = myFields.some((mf) => mf.id === field.id);
      const preFilledValue = initValues[field.id] || field.value || '';
      // Guard against malformed field definitions. A missing/NaN page number
      // or coordinate caused stamps to land on page 1 (the cover) on
      // re-present for some contracts — skip rather than render at (0,0).
      const pageIndex = Number(field.page) > 0 ? Number(field.page) : 1;
      const pageInfo = doc.getPageInfo(pageIndex);
      if (!pageInfo) return;
      if (
        typeof field.x !== 'number' || Number.isNaN(field.x) ||
        typeof field.y !== 'number' || Number.isNaN(field.y) ||
        typeof field.width !== 'number' || Number.isNaN(field.width) ||
        typeof field.height !== 'number' || Number.isNaN(field.height)
      ) {
        console.warn('[ContractApryse] skipping field with invalid coords', field.id, {
          page: field.page, x: field.x, y: field.y, width: field.width, height: field.height,
        });
        return;
      }
      const { width: pageW, height: pageH } = pageInfo;
      const x = (field.x / 100) * pageW;
      const y = (field.y / 100) * pageH;
      const w = (field.width / 100) * pageW;
      const h = (field.height / 100) * pageH;

      const isSig = field.type === 'signature' || field.type === 'initials';
      const isCheck = field.type === 'checkbox';

      // ── Read-only field (other role's field, show pre-filled value) ──
      if (!isMine) {
        if (isSig && preFilledValue && preFilledValue.startsWith('data:image')) {
          const stamp = new Annots.StampAnnotation();
          stamp.PageNumber = pageIndex;
          stamp.X = x; stamp.Y = y; stamp.Width = w; stamp.Height = h;
          stamp.setImageData(preFilledValue);
          stamp.Author = 'field-readonly';
          stamp.ReadOnly = true;
          stamp.setCustomData('permanent', 'true');
          annotManager.addAnnotation(stamp);
          fieldAnnotationsRef.current.set(field.id, stamp);
        } else if (isSig && preFilledValue) {
          // Signature field with a plain-text value (e.g. a printed name
          // stored on the template before the actual signature is drawn).
          // Render as read-only text so participants see what the host has
          // on their copy.
          const ro = new Annots.FreeTextAnnotation(Annots.FreeTextAnnotation.Intent.FreeText);
          ro.PageNumber = pageIndex;
          ro.X = x; ro.Y = y; ro.Width = w; ro.Height = h;
          ro.setContents(String(preFilledValue));
          ro.FontSize = `${Math.min(Math.max(h * 0.5, 8), 14)}pt`;
          ro.TextColor = new Annots.Color(0, 0, 0);
          ro.TextAlign = 'left';
          ro.StrokeThickness = 0;
          ro.StrokeColor = new Annots.Color(0, 0, 0, 0);
          ro.FillColor = new Annots.Color(0, 0, 0, 0);
          ro.Author = 'field-readonly';
          ro.ReadOnly = true;
          annotManager.addAnnotation(ro);
          fieldAnnotationsRef.current.set(field.id, ro);
        }
        // Also add ALL text/date fields to the React overlay (read-only).
        // Important: add them even WITHOUT a pre-filled value so that
        // updates arriving later via socket broadcast have a slot to
        // render into. Empty fields just show the placeholder until the
        // other side fills them.
        if (!isSig && !isCheck) {
          if (!pendingTextFields) pendingTextFields = [];
          pendingTextFields.push({
            field: { ...field, _readOnly: true },
            pageIndex, x, y, w, h,
            preFilledValue: preFilledValue || '',
          });
        } else if (isCheck) {
          // Always add a read-only checkbox annotation (even when unchecked)
          // so later fieldValue sync events have a slot to toggle. Without
          // this, checking a box on the host side never updates the
          // participant's PDF because there's no annotation to redraw.
          const isChecked = preFilledValue === 'true';
          const ro = new Annots.FreeTextAnnotation(Annots.FreeTextAnnotation.Intent.FreeText);
          ro.PageNumber = pageIndex;
          ro.X = x; ro.Y = y; ro.Width = w; ro.Height = h;
          ro.setContents(isChecked ? '\u2713' : '');
          ro.FontSize = `${Math.min(h * 0.7, 22)}pt`;
          ro.TextColor = new Annots.Color(0, 0, 0);
          ro.TextAlign = 'center';
          ro.StrokeThickness = 1;
          ro.StrokeColor = new Annots.Color(60, 130, 240, 0.6);
          ro.FillColor = isChecked
            ? new Annots.Color(60, 130, 240, 0.15)
            : new Annots.Color(60, 130, 240, 0.06);
          ro.Author = 'field-readonly';
          ro.ReadOnly = true;
          ro.setCustomData('fieldType', 'checkbox');
          annotManager.addAnnotation(ro);
          fieldAnnotationsRef.current.set(field.id, ro);
        } else if (preFilledValue && preFilledValue !== 'false' && !isSig) {
          const ro = new Annots.FreeTextAnnotation(Annots.FreeTextAnnotation.Intent.FreeText);
          ro.PageNumber = pageIndex;
          ro.X = x; ro.Y = y; ro.Width = w; ro.Height = h;
          ro.setContents(String(preFilledValue));
          ro.FontSize = `${Math.min(Math.max(h * 0.5, 8), 14)}pt`;
          ro.TextColor = new Annots.Color(0, 0, 0);
          ro.TextAlign = 'left';
          ro.StrokeThickness = 0;
          ro.StrokeColor = new Annots.Color(0, 0, 0, 0);
          ro.FillColor = new Annots.Color(0, 0, 0, 0);
          ro.Author = 'field-readonly';
          ro.ReadOnly = true;
          annotManager.addAnnotation(ro);
          fieldAnnotationsRef.current.set(field.id, ro);
        }
        return; // skip interactive widget creation
      }

      // ── Interactive field (this user's field) ──
      if (isSig) {
        // Already signed? Show stamp. Otherwise show clickable placeholder.
        if (preFilledValue && preFilledValue.startsWith('data:image')) {
          const stamp = new Annots.StampAnnotation();
          stamp.PageNumber = pageIndex;
          stamp.X = x; stamp.Y = y; stamp.Width = w; stamp.Height = h;
          stamp.setImageData(preFilledValue);
          stamp.Author = 'field-inline';
          stamp.ReadOnly = true;
          stamp.setCustomData('fieldId', field.id);
          stamp.setCustomData('fieldType', field.type);
          stamp.setCustomData('permanent', 'true');
          annotManager.addAnnotation(stamp);
          fieldAnnotationsRef.current.set(field.id, stamp);
        } else {
          const rect = new Annots.FreeTextAnnotation(Annots.FreeTextAnnotation.Intent.FreeText);
          rect.PageNumber = pageIndex;
          rect.X = x; rect.Y = y; rect.Width = w; rect.Height = h;
          rect.setContents(field.type === 'initials' ? 'Click to add initials' : 'Click to sign');
          rect.FontSize = `${Math.min(Math.max(h * 0.35, 8), 13)}pt`;
          rect.TextColor = new Annots.Color(100, 100, 100);
          rect.TextAlign = 'center';
          rect.StrokeThickness = 2;
          // Match the ContractModal editor's role colors so hosts/participants
          // can tell their signature fields apart at a glance:
          //   host        -> orange (#ff9800 ≈ 255,152,0)
          //   participant -> blue   (#2196f3 ≈ 33,150,243)
          if (field.role === 'host') {
            rect.StrokeColor = new Annots.Color(255, 152, 0);
            rect.FillColor = new Annots.Color(255, 152, 0, 0.08);
          } else {
            rect.StrokeColor = new Annots.Color(33, 150, 243);
            rect.FillColor = new Annots.Color(33, 150, 243, 0.08);
          }
          rect.Author = 'field-inline';
          rect.ReadOnly = true;
          rect.setCustomData('fieldId', field.id);
          rect.setCustomData('fieldType', field.type);
          annotManager.addAnnotation(rect);
          fieldAnnotationsRef.current.set(field.id, rect);
        }
      } else if (isCheck) {
        const cb = new Annots.FreeTextAnnotation(Annots.FreeTextAnnotation.Intent.FreeText);
        cb.PageNumber = pageIndex;
        cb.X = x; cb.Y = y; cb.Width = w; cb.Height = h;
        cb.setContents(preFilledValue === 'true' ? '\u2713' : '');
        cb.FontSize = `${Math.min(h * 0.7, 22)}pt`;
        cb.TextColor = new Annots.Color(0, 0, 0);
        cb.TextAlign = 'center';
        cb.StrokeThickness = 2;
        cb.StrokeColor = new Annots.Color(60, 130, 240);
        cb.FillColor = preFilledValue === 'true'
          ? new Annots.Color(60, 130, 240, 0.15)
          : new Annots.Color(60, 130, 240, 0.06);
        cb.Author = 'field-inline';
        cb.ReadOnly = true;
        cb.setCustomData('fieldId', field.id);
        cb.setCustomData('fieldType', 'checkbox');
        annotManager.addAnnotation(cb);
        fieldAnnotationsRef.current.set(field.id, cb);
      } else {
        // Text / date: store field info for deferred HTML input injection.
        // We collect these and inject after a short delay to ensure pages
        // are rendered in the iframe DOM.
        if (!pendingTextFields) pendingTextFields = [];
        pendingTextFields.push({ field, pageIndex, x, y, w, h, preFilledValue });
      }
    });

    // Draw only actual Apryse annotations (skip HTML input entries)
    const annotsToDraw = Array.from(fieldAnnotationsRef.current.values())
      .filter((a) => a && typeof a.PageNumber === 'number');
    if (annotsToDraw.length) {
      annotManager.drawAnnotationsFromList(annotsToDraw);
    }
    setTimeout(() => { applyingRemoteRef.current = false; }, 50);

    // --- Text/date fields: calculate positions for React overlay inputs ---
    // Instead of injecting into the iframe DOM (unreliable), we render
    // React <input> elements in an overlay div. We calculate positions
    // using the Apryse viewer's coordinate system.
    if (pendingTextFields && pendingTextFields.length > 0) {
      console.log('[ContractApryse] pendingTextFields:', pendingTextFields.length,
        pendingTextFields.map(p => ({ id: p.field.id, label: p.field.label, page: p.pageIndex })));

      // Mark fields in ref so validation can read from React state
      pendingTextFields.forEach(({ field }) => {
        fieldAnnotationsRef.current.set(field.id, { _reactInput: true, _fieldType: field.type });
      });

      // Store field definitions for the React overlay renderer
      const inputDefs = pendingTextFields.map(({ field, pageIndex: pgIdx, x: fx, y: fy, w: fw, h: fh, preFilledValue: pfv }) => ({
        fieldId: field.id,
        fieldType: field.type,
        label: field.label || '',
        placeholder: field.placeholder || '',
        role: field.role || 'participant',
        value: pfv || '',
        pageIndex: pgIdx,
        rawX: fx, rawY: fy, rawW: fw, rawH: fh,
        readOnly: !!field._readOnly,
      }));
      console.log('[ContractApryse] Setting inlineInputPositions:', inputDefs.length);
      setInlineInputPositions(inputDefs);
    } else {
      console.log('[ContractApryse] No pendingTextFields. myFields:', myFields.length,
        'allFields:', (contract?.editable_fields || []).length,
        'isHost:', isHost,
        'myFields types:', myFields.map(f => ({ type: f.type, role: f.role, label: f.label })));
    }

    console.log('[ContractApryse] Inline annotations created:', annotsToDraw.length,
      'text fields as overlay:', pendingTextFields?.length || 0);

    // --- Click handler: signature + checkbox fields ---
    annotManager.addEventListener('annotationSelected', (annots, action) => {
      if (action !== 'selected') return;
      (annots || []).forEach((a) => {
        const fType = a.getCustomData?.('fieldType');
        const fId = a.getCustomData?.('fieldId');
        if (!fType || !fId) return;

        if (fType === 'signature' || fType === 'initials') {
          const fieldsNow = isHost
            ? (contract?.editable_fields || []).filter((ff) => ff.role === 'host')
            : (contract?.editable_fields || []).filter((ff) => ff.role !== 'host');
          const f = fieldsNow.find((mf) => mf.id === fId);
          if (f) {
            annotManager.deselectAnnotation(a);
            handleSign(f);
          }
        } else if (fType === 'checkbox') {
          annotManager.deselectAnnotation(a);
          setFieldValues((prev) => {
            const cur = prev[fId];
            const next = cur === 'true' ? 'false' : 'true';
            applyingRemoteRef.current = true;
            a.setContents(next === 'true' ? '\u2713' : '');
            a.FillColor = next === 'true'
              ? new Annots.Color(60, 130, 240, 0.15)
              : new Annots.Color(60, 130, 240, 0.06);
            annotManager.redrawAnnotation(a);
            setTimeout(() => { applyingRemoteRef.current = false; }, 30);
            // Broadcast checkbox change so the other side can sync it
            if (typeof emitContractEvent === 'function') {
              const fieldDef = (contract?.editable_fields || []).find((f) => f.id === fId);
              const fieldRole = fieldDef?.role || 'participant';
              emitContractEvent('fieldValue', {
                fromId: currentUserId || '',
                fromName: currentUserName || (isHost ? 'Host' : 'Participant'),
                fromRole: isHost ? 'host' : 'participant',
                forParticipantId: isHost && fieldRole === 'host' ? null : (isHost ? null : (currentUserId || '')),
                fieldId: fId,
                fieldRole,
                value: next,
              });
            }
            return { ...prev, [fId]: next };
          });
        }
        // Text/date fields are HTML inputs — no annotation handling needed
      });
    });

    // Text field values are read directly from annotations at submit
    // time via getContents() — no real-time sync needed.
  };

  // --- Sync signature images back onto PDF when fieldValues change ---
  // (Signatures are set via the modal, not inline editing, so we need
  // to replace the placeholder annotation with a stamp image.)
  useEffect(() => {
    const instance = instanceRef.current;
    const annotManager = annotManagerRef.current;
    if (!instance || !annotManager) return;
    const { Core } = instance;
    const docViewer = documentViewerRef.current;
    if (!docViewer) return;
    const doc = docViewer.getDocument();
    if (!doc) return;

    // Iterate ALL fields (host + participant) so participants render
    // host signatures that arrive via real-time broadcast, and host
    // renders any participant signatures they receive.
    const allFields = contract?.editable_fields || [];

    // Host sees only the SELECTED participant's values (if any) merged with
    // their own host-field values. If no participant is selected, host
    // sees only their own values — keeping each participant's signatures
    // and checkbox choices separate (no cross-contamination).
    const mergedValues = { ...fieldValues };
    if (isHost && selectedParticipantId) {
      Object.assign(mergedValues, participantFieldValues[selectedParticipantId] || {});
    }

    allFields.forEach((field) => {
      const isSig = field.type === 'signature' || field.type === 'initials';
      if (!isSig) return;
      const value = mergedValues[field.id];
      const existingAnnot = fieldAnnotationsRef.current.get(field.id);

      // If there's no value for this field but we previously had a permanent
      // stamp (from a different participant), remove it so we don't show
      // the wrong signature.
      if ((!value || !value.startsWith('data:image'))) {
        if (existingAnnot && existingAnnot.getCustomData?.('permanent') === 'true') {
          applyingRemoteRef.current = true;
          try {
            annotManager.deleteAnnotation(existingAnnot, { imported: true });
          } catch (_) {}
          fieldAnnotationsRef.current.delete(field.id);
          setTimeout(() => { applyingRemoteRef.current = false; }, 30);
        }
        return;
      }

      const newHash = value.slice(0, 100);
      // Skip if stamp already exists with SAME image (prevents blinking).
      if (existingAnnot && existingAnnot.getCustomData?.('permanent') === 'true') {
        const existingData = existingAnnot.getCustomData?.('sigHash') || '';
        if (existingData === newHash) return;
        // Otherwise: different signature, OR initial stamp without hash.
        // Just update the image in place instead of delete+recreate.
        applyingRemoteRef.current = true;
        try {
          existingAnnot.setImageData(value);
          existingAnnot.setCustomData('sigHash', newHash);
          annotManager.redrawAnnotation(existingAnnot);
        } catch (_) {}
        setTimeout(() => { applyingRemoteRef.current = false; }, 30);
        return;
      }

      const pageIndex = field.page || 1;
      const pageInfo = doc.getPageInfo(pageIndex);
      if (!pageInfo) return;
      const { width: pageW, height: pageH } = pageInfo;
      const x = (field.x / 100) * pageW;
      const y = (field.y / 100) * pageH;
      const w = (field.width / 100) * pageW;
      const h = (field.height / 100) * pageH;

      applyingRemoteRef.current = true;
      // Remove any existing placeholder annotation first
      if (existingAnnot) {
        try {
          annotManager.deleteAnnotation(existingAnnot, { imported: true });
        } catch (_) {}
      }

      const stamp = new Core.Annotations.StampAnnotation();
      stamp.PageNumber = pageIndex;
      stamp.X = x; stamp.Y = y; stamp.Width = w; stamp.Height = h;
      stamp.setImageData(value);
      stamp.Author = 'field-inline';
      stamp.ReadOnly = true;
      stamp.setCustomData('fieldId', field.id);
      stamp.setCustomData('fieldType', field.type);
      stamp.setCustomData('permanent', 'true');
      // Store a short hash of the image data so we can detect when the
      // value changes (e.g. host switches between participants).
      stamp.setCustomData('sigHash', value.slice(0, 100));
      annotManager.addAnnotation(stamp);
      annotManager.redrawAnnotation(stamp);
      fieldAnnotationsRef.current.set(field.id, stamp);
      setTimeout(() => { applyingRemoteRef.current = false; }, 30);
    });
  }, [fieldValues, contract?.editable_fields, isHost, selectedParticipantId, participantFieldValues]);

  // --- Sync checkbox states from field values to annotations ---
  useEffect(() => {
    const instance = instanceRef.current;
    const annotManager = annotManagerRef.current;
    if (!instance || !annotManager) return;
    const { Core } = instance;
    const allFields = contract?.editable_fields || [];

    // Host sees only the SELECTED participant's checkbox states; no merging
    // across all participants (keeps each participant's checks isolated).
    const mergedValues = { ...fieldValues };
    if (isHost && selectedParticipantId) {
      Object.assign(mergedValues, participantFieldValues[selectedParticipantId] || {});
    }

    allFields.forEach((field) => {
      if (field.type !== 'checkbox') return;
      const annot = fieldAnnotationsRef.current.get(field.id);
      if (!annot || !annot.setContents) return;
      const value = mergedValues[field.id];
      const shouldBeChecked = value === 'true';
      const currentContents = annot.getContents?.() || '';
      const isCurrentlyChecked = currentContents === '\u2713';
      if (shouldBeChecked !== isCurrentlyChecked) {
        applyingRemoteRef.current = true;
        annot.setContents(shouldBeChecked ? '\u2713' : '');
        annot.FillColor = shouldBeChecked
          ? new Core.Annotations.Color(60, 130, 240, 0.15)
          : new Core.Annotations.Color(60, 130, 240, 0.06);
        annotManager.redrawAnnotation(annot);
        setTimeout(() => { applyingRemoteRef.current = false; }, 30);
      }
    });
  }, [fieldValues, contract?.editable_fields, isHost, selectedParticipantId, participantFieldValues]);

  const handleSignatureFieldClick = useCallback(
    (field) => handleSign(field),
    [handleSign]
  );

  const handleDecline = useCallback(async () => {
    // Start geolocation inside the click (direct user gesture) before
    // the synchronous prompt dialog.
    let locationPromise;
    try {
      locationPromise = capturePreciseLocation();
    } catch (_) {
      locationPromise = Promise.resolve({ status: 'error', reason: 'sync' });
    }

    const reason = window.prompt(
      'Decline to sign this contract?\n\nOptional: provide a reason for the audit log.',
      ''
    );
    if (reason === null) return; // user cancelled
    const preciseLocation = await locationPromise;
    logContractAuditEvent({
      contractElementId,
      meetingId,
      eventType: 'declined',
      actor: {
        id: currentUserId || '',
        name: currentUserName || 'Participant',
        email: currentUserEmail || '',
        role: isHost ? 'host' : 'participant',
      },
      metadata: { reason: reason || '', preciseLocation },
      documentVersion: documentVersionRef.current,
    });
    toast.success('Declined. A record has been added to the audit log.');
    if (typeof onClose === 'function') onClose();
  }, [
    contractElementId,
    meetingId,
    currentUserId,
    currentUserName,
    currentUserEmail,
    isHost,
    onClose,
  ]);

  const handleDownloadCertificate = useCallback(() => {
    downloadContractCertificate({
      contractElementId,
      contractName: contract?.name || 'Contract',
    });
  }, [contractElementId, contract?.name]);

  const handlePresent = useCallback(() => {
    if (typeof onSendToParticipants !== 'function') {
      toast.error('Presentation handler missing');
      return;
    }

    const fireAudit = (eventType) => {
      logContractAuditEvent({
        contractElementId,
        meetingId,
        eventType,
        actor: {
          id: currentUserId || '',
          name: currentUserName || 'Host',
          email: currentUserEmail || '',
          role: 'host',
        },
        metadata: { fileUrl: contract?.file_url || '' },
        documentVersion: documentVersionRef.current,
      });
    };
    try {
      // Include host's filled field values so participants see them pre-filled.
      // Always rebuild editable_fields (don't guard on currentFV having keys)
      // so existing template-stored f.value stays intact and any live host
      // edits override it. Without this merge, participants only see what was
      // persisted on the Element doc, not the host's in-session edits.
      const currentFV = fieldValuesRef.current || {};
      const contractWithHostValues = { ...contract, type: 'contract' };
      contractWithHostValues.editable_fields = (contract?.editable_fields || []).map((f) => {
        const liveVal = currentFV[f.id];
        if (liveVal !== undefined && liveVal !== null && liveVal !== '') {
          return { ...f, value: liveVal };
        }
        return f; // keep template-stored f.value intact
      });
      const result = onSendToParticipants(contractWithHostValues);
      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            setPresented((prev) => {
              fireAudit(prev ? 'resent' : 'presented');
              return true;
            });
            toast.success('Contract sent to participants');
          })
          .catch((err) => {
            console.error('[ContractApryse] present failed', err);
            toast.error('Failed to send contract');
          });
      } else {
        setPresented((prev) => {
          fireAudit(prev ? 'resent' : 'presented');
          return true;
        });
        toast.success('Contract sent to participants');
      }
    } catch (err) {
      console.error('[ContractApryse] present threw', err);
      toast.error('Failed to send contract');
    }
  }, [
    contract,
    onSendToParticipants,
    editableFields,
    fieldValues,
    contractElementId,
    meetingId,
    currentUserId,
    currentUserName,
    currentUserEmail,
  ]);

  const handleStopPresenting = useCallback(() => {
    if (typeof onStopPresenting !== 'function') return;
    onStopPresenting();
    setPresented(false);
    logContractAuditEvent({
      contractElementId,
      meetingId,
      eventType: 'stopped',
      actor: {
        id: currentUserId || '',
        name: currentUserName || 'Host',
        email: currentUserEmail || '',
        role: 'host',
      },
      metadata: { fileUrl: contract?.file_url || '' },
      documentVersion: documentVersionRef.current,
    });
    toast.success('Contract removed from participants');
  }, [onStopPresenting, contractElementId, meetingId, currentUserId, currentUserName, currentUserEmail, contract?.file_url]);

  const handleSignatureComplete = useCallback(
    async (signatureDataUrl) => {
      setSignatureModalOpen(false);
      if (!signatureDataUrl) return;

      // If this was triggered from a specific editable field, store the
      // value in fieldValues (the form-panel path). If it was the
      // free-standing "Sign" toolbar button (no field), place an Apryse
      // stamp annotation on the current page (the original Chunk B path).
      if (activeSignatureField) {
        setFieldValues((prev) => ({
          ...prev,
          [activeSignatureField.id]: signatureDataUrl,
        }));
        // Broadcast the signature so other side sees it in real-time.
        // Host signatures broadcast to ALL participants (no forParticipantId).
        // Participant signatures broadcast to the host.
        if (typeof emitContractEvent === 'function') {
          emitContractEvent('fieldValue', {
            fromId: currentUserId || '',
            fromName: currentUserName || (isHost ? 'Host' : 'Participant'),
            fromRole: isHost ? 'host' : 'participant',
            forParticipantId: isHost ? null : (currentUserId || ''),
            fieldId: activeSignatureField.id,
            value: signatureDataUrl,
          });
        }
        setActiveSignatureField(null);
        // Still emit signed notice + audit below
      } else {
        // Place stamp annotation on the PDF (free-standing signature)
        const instance = instanceRef.current;
        const annotManager = annotManagerRef.current;
        const documentViewer = documentViewerRef.current;
        if (!instance || !annotManager || !documentViewer) return;

        try {
          const { Core } = instance;
          const { Annotations } = Core;
          const page = documentViewer.getCurrentPage();
          const stamp = new Annotations.StampAnnotation();
          stamp.PageNumber = page;
          const pageInfo = documentViewer.getDocument().getPageInfo(page);
          const sigW = Math.min(220, pageInfo.width * 0.4);
          const sigH = sigW / 3;
          stamp.X = (pageInfo.width - sigW) / 2;
          stamp.Y = pageInfo.height - sigH - 60;
          stamp.Width = sigW;
          stamp.Height = sigH;
          stamp.Author = currentUserName;
          stamp.Subject = 'Stamp';
          stamp.setImageData(signatureDataUrl, true);
          try {
            stamp.setCustomData('permanent', 'true');
            stamp.setCustomData('signer', currentUserName);
            stamp.setCustomData('signedAt', new Date().toISOString());
          } catch (_) {}
          annotManager.addAnnotation(stamp);
          annotManager.drawAnnotationsFromList([stamp]);
        } catch (err) {
          console.error('[ContractApryse] stamp failed', err);
          toast.error('Failed to place signature');
          return;
        }
      }

      // Notify host + audit (applies to both field-based and stamp-based)
      if (!isHost) {
        emitContractEvent('signedNotice', {
          participantId: currentUserId || '',
          participantName: currentUserName || 'Participant',
          at: new Date().toISOString(),
        });
      }

      let preciseLocation;
      try {
        preciseLocation = pendingLocationRef.current
          ? await pendingLocationRef.current
          : await capturePreciseLocation();
      } catch (_) {
        preciseLocation = { status: 'error', reason: 'promise_rejected' };
      } finally {
        pendingLocationRef.current = null;
      }
      logContractAuditEvent({
        contractElementId,
        meetingId,
        eventType: 'signed',
        actor: {
          id: currentUserId || '',
          name: currentUserName || 'Participant',
          email: currentUserEmail || '',
          role: isHost ? 'host' : 'participant',
        },
        metadata: { preciseLocation },
        documentVersion: documentVersionRef.current,
      });
    },
    [
      currentUserName,
      currentUserId,
      currentUserEmail,
      contractElementId,
      meetingId,
      isHost,
      activeSignatureField,
      emitContractEvent,
    ]
  );

  const handleSubmitSigned = useCallback(async () => {
    const instance = instanceRef.current;
    const annotManager = annotManagerRef.current;
    const documentViewer = documentViewerRef.current;
    if (!instance || !annotManager || !documentViewer) {
      toast.error('Viewer not ready');
      return;
    }

    // --- Validate ALL required fields ---
    // Read text/date values from injected HTML inputs.
    // Read checkbox from annotation content.
    // Read signatures from React state (set via modal).
    const currentFV = { ...fieldValuesRef.current };

    // Harvest live values from annotations and React state
    fieldAnnotationsRef.current.forEach((entry, fId) => {
      if (fId.startsWith('__')) return;
      if (entry?._reactInput) {
        // React-rendered input — values are already in fieldValues via onChange
      } else if (entry?._htmlInput) {
        // HTML input element — read value directly from DOM
        const val = entry._htmlInput.value || '';
        if (val) currentFV[fId] = val;
      } else if (entry?.getCustomData) {
        // Apryse annotation (checkbox)
        const fType = entry.getCustomData?.('fieldType');
        if (fType === 'checkbox') {
          const content = entry.getContents?.() || '';
          if (content === '\u2713') currentFV[fId] = 'true';
        }
      }
    });

    const missingFieldsList = [];
    myFields.forEach((f) => {
      const val = currentFV[f.id];
      const isSig = f.type === 'signature' || f.type === 'initials';
      const isCheck = f.type === 'checkbox';

      if (isSig) {
        if (!val || !val.startsWith('data:image')) {
          missingFieldsList.push({ id: f.id, label: f.label || f.type, page: f.page || 1 });
        }
      } else if (f.required && !isCheck) {
        // Checkboxes are selections (e.g. Male/Female) — never block submission.
        // Only text/date required fields are validated.
        if (!val || (typeof val === 'string' && !val.trim())) {
          missingFieldsList.push({ id: f.id, label: f.label || f.type, page: f.page || 1 });
        }
      }
    });

    if (missingFieldsList.length > 0) {
      toast.error(
        (t) => {
          const scrollToField = (fieldId, page) => {
            toast.dismiss(t.id);
            const dv = documentViewerRef.current;
            if (dv) dv.setCurrentPage(page);
            const annot = fieldAnnotationsRef.current.get(fieldId);
            const am = annotManagerRef.current;
            if (annot && am) am.selectAnnotation(annot);
          };
          return React.createElement('div', { style: { fontSize: 13 } },
            React.createElement('div', { style: { fontWeight: 600, marginBottom: 6 } },
              `${missingFieldsList.length} required field${missingFieldsList.length > 1 ? 's' : ''} missing:`
            ),
            missingFieldsList.map((mf) =>
              React.createElement('div', {
                key: mf.id,
                onClick: () => scrollToField(mf.id, mf.page),
                style: { cursor: 'pointer', padding: '3px 0', color: '#93c5fd', textDecoration: 'underline' },
              }, `→ ${mf.label} (page ${mf.page})`)
            )
          );
        },
        { duration: 8000 }
      );
      return;
    }

    // If no editable fields at all, require at least one signature annotation
    if (myFields.length === 0) {
      const permanent = annotManager
        .getAnnotationsList()
        .filter((a) => isPermanentAnnotation(a));
      if (permanent.length === 0) {
        toast.error('Please add your signature before submitting');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Submit path no longer bakes the PDF in Apryse — backend regenerates
      // the filled PDF with the same pipeline used by the dashboard's
      // "Regenerate" action so positioning is identical across both flows.
      const allEditableFields = contract?.editable_fields || [];
      const currentFV = { ...fieldValuesRef.current };

      // Build filledFields (all editable fields, value sourced from live
      // state or template defaults). Send JSON — backend regenerates PDF.
      const submitValues = { ...currentFV };
      const filledFields = allEditableFields.map((f) => ({
        fieldId: f.id,
        label: f.label || f.type,
        type: f.type,
        value: submitValues[f.id] || f.value || '',
      }));

      const res = await requests.post('/contract/submit-signed', {
        contractElementId,
        meetingId,
        participantId: currentUserId || 'unknown',
        participantName: currentUserName || 'Participant',
        participantEmail: currentUserEmail || '',
        originalDocumentUrl: contract?.file_url || '',
        documentVersion: documentVersionRef.current || '',
        filledFields: JSON.stringify(filledFields),
      });

      if (res?.success) {
        setSubmissionResult({
          downloadUrl: res.downloadUrl || '',
          submittedAt: new Date().toISOString(),
          participantName: currentUserName || 'Participant',
        });
        toast.success('Contract signed and submitted');
        if (typeof onUpdate === 'function') {
          onUpdate({ ...contract, submitted: true, downloadUrl: res.downloadUrl });
        }
        // Clean up the draft
        try {
          await requests.delete(
            `/contract/draft/${encodeURIComponent(contractElementId)}/${encodeURIComponent(meetingId)}/${encodeURIComponent(draftUserId)}`
          );
        } catch (_) {}
      } else {
        toast.error(res?.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ContractApryse] submit failed', err);
      toast.error(`Submission failed: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }, [
    contract,
    contractElementId,
    meetingId,
    currentUserId,
    currentUserName,
    currentUserEmail,
    onUpdate,
    myFields,
    fieldValues,
  ]);

  return (
    <>
      {/* Floating chip when minimized — brings contract back */}
      {minimized && (
        <button
          type="button"
          onClick={() => {
            setMinimized(false);
            // Re-request participant values after restore — during minimize
            // the viewer was hidden and may have missed live updates. The
            // hostOpened event prompts every participant to rebroadcast
            // their current field values.
            if (isHost && typeof emitContractEvent === 'function') {
              setTimeout(() => {
                emitContractEvent('hostOpened', {
                  hostId: currentUserId || '',
                  hostName: currentUserName || 'Host',
                });
              }, 300);
            }
          }}
          className="fixed bottom-24 right-6 z-[50] flex items-center gap-2 px-4 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold shadow-xl transition-transform hover:scale-105"
          title="Restore the contract"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
            <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
            <path d="M8 17l2-2 2 2 4-4" />
          </svg>
          Open Contract
        </button>
      )}
    <div
      ref={containerRef}
      className={`fixed inset-0 z-[60] flex flex-col bg-[#0D1117] overflow-hidden ${minimized ? 'hidden' : ''}`}
    >
      <div className="flex-shrink-0 sticky top-0 z-30">
        <ContractAnnotationToolbar
          isHost={isHost}
          activeToolId={activeToolId}
          onSelectTool={handleSelectTool}
          activeColorId={activeColorId}
          onSelectColor={handleSelectColor}
          thickness={thickness}
          onThicknessChange={handleThicknessChange}
          fadeSeconds={fadeSeconds}
          onFadeSecondsChange={handleFadeSecondsChange}
          onClearAll={handleClearAll}
          onSign={!isHost ? handleSign : undefined}
          onSubmitSigned={!isHost ? handleSubmitSigned : undefined}
          onDecline={!isHost ? handleDecline : undefined}
          onPresent={isHost ? handlePresent : undefined}
          presented={presented}
          onStopPresenting={isHost ? handleStopPresenting : undefined}
          onDownloadCertificate={isHost ? handleDownloadCertificate : undefined}
          onMinimize={() => {
            setMinimized(true);
          }}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onZoomReset={handleZoomReset}
          currentZoom={currentZoom}
          onSearch={handleSearch}
          onClose={onClose}
          connected={!!meetingState?.connected}
          signedNotices={isHost ? signedNotices : undefined}
          showSignedList={showSignedList}
          onToggleSignedList={() => setShowSignedList((v) => !v)}
        />
      </div>

      <div className="flex-1 flex min-h-0">
        {/* PDF Viewer */}
        <div className="flex-1 relative min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 pointer-events-none">
              Loading contract…
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center text-red-400">
              {error}
            </div>
          )}
          <div ref={viewerRef} className="w-full h-full" />

          {/* Host-only: Participant viewer panel */}
          {isHost && presented && (
            <div className="absolute top-2 right-2 z-40">
              <button
                type="button"
                onClick={() => setShowParticipantPanel((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0D1117] border border-[#30363D] text-white text-xs font-semibold shadow-lg hover:bg-[#161B22]"
                title="View & fill for participants"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                {selectedParticipantId
                  ? `Viewing: ${activeParticipants.find((p) => p.id === selectedParticipantId)?.name || 'Participant'}`
                  : `Participants (${activeParticipants.length})`}
                <span className="opacity-60">▾</span>
              </button>
              {showParticipantPanel && (
                <div className="mt-2 w-64 max-h-80 overflow-y-auto rounded-lg bg-[#0D1117] border border-[#30363D] shadow-xl">
                  <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 border-b border-[#30363D]">
                    Live Participants
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedParticipantId(null);
                      setShowParticipantPanel(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 ${
                      !selectedParticipantId ? 'bg-purple-600/20 text-purple-300' : 'text-gray-300'
                    }`}
                  >
                    My view (host)
                  </button>
                  {activeParticipants.length === 0 && (
                    <div className="px-3 py-3 text-xs text-gray-500 italic">
                      No participants have started filling yet.
                    </div>
                  )}
                  {activeParticipants.map((p) => {
                    const values = participantFieldValues[p.id] || {};
                    const filledCount = Object.values(values).filter((v) => v && v !== 'false').length;
                    const hasSigned = signedNotices.some((s) => s.participantId === p.id);
                    const sub = submissionsByParticipant[p.id];
                    return (
                      <div
                        key={p.id}
                        className={`border-t border-[#30363D] ${
                          selectedParticipantId === p.id ? 'bg-purple-600/20' : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedParticipantId(p.id);
                            setShowParticipantPanel(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 ${
                            selectedParticipantId === p.id ? 'text-purple-300' : 'text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium truncate">{p.name}</span>
                            <div className="flex items-center gap-1">
                              {sub && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-600/30 text-blue-300">
                                  Submitted
                                </span>
                              )}
                              {hasSigned && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-600/30 text-green-300">
                                  Signed
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {filledCount} field{filledCount !== 1 ? 's' : ''} filled
                          </div>
                        </button>
                        {sub?.url && (
                          <a
                            href={sub.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 px-3 pb-2 text-[11px] text-blue-300 hover:text-blue-200"
                            title="Download signed PDF"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download signed PDF
                          </a>
                        )}
                      </div>
                    );
                  })}
                  {selectedParticipantId && (
                    <div className="px-3 py-2 text-[10px] text-amber-300 border-t border-[#30363D] bg-amber-600/10">
                      You can now type into fields to fill them on this participant's behalf.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Inline text/date inputs — React overlay synced with Apryse scroll/zoom */}
          <InlineFieldOverlay
            fields={inlineInputPositions}
            documentViewerRef={documentViewerRef}
            instanceRef={instanceRef}
            viewerRef={viewerRef}
            liveValues={(() => {
              if (!isHost) return fieldValues;
              // Host: merge host's own values (for host fields) +
              // selected participant's values (for participant fields)
              const merged = { ...fieldValues };
              if (selectedParticipantId) {
                Object.assign(merged, participantFieldValues[selectedParticipantId] || {});
              }
              return merged;
            })()}
            onFieldChange={(fieldId, value) => {
              // Find the field's role to decide where to store the value
              const fieldDef = (contract?.editable_fields || []).find((f) => f.id === fieldId);
              const fieldRole = fieldDef?.role || 'participant';

              if (isHost && fieldRole === 'host') {
                // Host filling their own field — store in fieldValues, broadcast to all
                setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
              } else if (isHost && selectedParticipantId) {
                // Host filling a participant field on behalf
                setParticipantFieldValues((prev) => ({
                  ...prev,
                  [selectedParticipantId]: {
                    ...(prev[selectedParticipantId] || {}),
                    [fieldId]: value,
                  },
                }));
              } else if (isHost) {
                // Host viewing without selecting a participant but typing in
                // a participant field — warn them they need to select someone
                console.warn('[Contract] Host typed in participant field without selecting a participant. Select a participant first.');
                return;
              } else {
                // Participant typing in their own field
                setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
              }
              // Decide who this update is FOR:
              // - Host filling host field: forParticipantId = null (applies to ALL)
              // - Host filling participant field: forParticipantId = selectedParticipantId
              // - Participant typing own field: forParticipantId = themselves (no-op for others)
              let forParticipantId;
              if (isHost && fieldRole === 'host') {
                forParticipantId = null; // broadcast to all
              } else if (isHost) {
                forParticipantId = selectedParticipantId;
              } else {
                forParticipantId = currentUserId || '';
              }
              const payload = {
                fromId: currentUserId || '',
                fromName: currentUserName || (isHost ? 'Host' : 'Participant'),
                fromRole: isHost ? 'host' : 'participant',
                forParticipantId,
                fieldId,
                fieldRole,
                value,
              };
              console.log('[Contract] Broadcasting fieldValue:', payload);
              if (typeof emitContractEvent === 'function') {
                emitContractEvent('fieldValue', payload);
              } else {
                console.warn('[Contract] emitContractEvent not available!');
              }
            }}
          />
        </div>

        {/* Fields are rendered inline on the PDF — no sidebar panel */}
      </div>

      <SignatureModal
        isOpen={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setActiveSignatureField(null);
        }}
        onConfirm={handleSignatureComplete}
        fieldLabel={activeSignatureField?.label || 'Signature'}
        fieldType={activeSignatureField?.type || 'signature'}
        defaultName={currentUserName}
      />

      {/* Toaster — scoped to this overlay so react-hot-toast renders
          above the viewer regardless of app-level Toaster presence. */}
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#0D1117',
            color: '#fff',
            border: '1px solid #30363D',
            fontSize: 13,
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
        containerStyle={{ zIndex: 9999 }}
      />

      {/* Submitting overlay */}
      {submitting && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="bg-[#0D1117] border border-[#30363D] rounded-xl px-8 py-6 text-white text-sm flex items-center gap-3">
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Submitting signed contract…
          </div>
        </div>
      )}

      {/* Submission success card */}
      {submissionResult && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0D1117] border border-emerald-600/40 rounded-2xl px-8 py-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-600/20 flex items-center justify-center">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Contract Signed & Submitted
            </h2>
            <p className="text-sm text-gray-400 mb-1">
              {submissionResult.participantName}
            </p>
            <p className="text-xs text-gray-500 mb-6">
              {new Date(submissionResult.submittedAt).toLocaleString()}
            </p>
            <div className="flex flex-col gap-2">
              {submissionResult.downloadUrl && (() => {
                const base = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
                const qs = new URLSearchParams({
                  url: submissionResult.downloadUrl,
                  name: contract?.name || 'signed-contract.pdf',
                  meetingId: meetingId || '',
                  actorName: currentUserName || '',
                  actorId: currentUserId || '',
                  actorEmail: currentUserEmail || '',
                  actorRole: isHost ? 'host' : 'participant',
                }).toString();
                const href = `${base}/contract/audit/${encodeURIComponent(
                  contractElementId
                )}/download?${qs}`;
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#21262D] hover:bg-[#30363D] text-white text-sm"
                  >
                    <Download size={16} />
                    Download signed copy
                  </a>
                );
              })()}
              <button
                type="button"
                onClick={() => {
                  setSubmissionResult(null);
                  if (typeof onClose === 'function') onClose();
                }}
                className="px-4 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
